import {
  CategoryKey,
  FIN_CATEGORY_BY_KEY,
  getCategoryExperience,
  isAdjustableExpense,
} from '#dictionary'

import type { TimelineRecord } from './timeline/TimelineRecord'
import type { AnnualAggregationMeta, FinancialRecordSummary } from './types'

export type AggregatedData = {
  income: AnnualAggregationMeta
  /**
   * What earning produces: FICA, federal and state income, capital gains,
   * NIIT and AMT.
   * Property tax is not here — it is charged on what you own, so it is counted
   * under `expenses`, where a user meets it (ZL-1252).
   */
  incomeTaxes: AnnualAggregationMeta
  expenses: AnnualAggregationMeta
  /**
   * The part of `expenses` the spend-less lever can actually move, declared per
   * category by `e_isAdjustable` — the same flag `adjustRecords` scales on.
   *
   * Not a partition with `propertyTaxes`. Loan interest and principal are in
   * neither, because the engine charges them against a liability record. What
   * the lever cannot move is `expenses - adjustableExpenses`.
   */
  adjustableExpenses: AnnualAggregationMeta
  /** The property tax inside `expenses`, charged on what you own. */
  propertyTaxes: AnnualAggregationMeta
  contributions: AnnualAggregationMeta
  cashFlow: AnnualAggregationMeta
  freeCash: AnnualAggregationMeta
  liabilities: AnnualAggregationMeta
  // TODO: ZL-645 — remove redundant `assets` field, derive from `assetsBreakdown` instead
  assets: AnnualAggregationMeta
  liquidAssets: AnnualAggregationMeta
  drawDowns: AnnualAggregationMeta
  assetsBreakdown: FinancialRecordSummary[]
}

type AssetDrawdown = {
  hasDrawdown: boolean
  drawdownAmount: number
}

const emptyMeta = (): AnnualAggregationMeta => ({
  totalValue: 0,
  recordsCount: 0,
  description: null,
})

export const deriveCashFlow = (
  income: AnnualAggregationMeta,
  expenses: AnnualAggregationMeta,
  incomeTaxes: AnnualAggregationMeta,
  contributions: AnnualAggregationMeta
): AnnualAggregationMeta => ({
  totalValue:
    income.totalValue -
    expenses.totalValue -
    incomeTaxes.totalValue -
    contributions.totalValue,
  recordsCount:
    income.recordsCount +
    expenses.recordsCount +
    incomeTaxes.recordsCount +
    contributions.recordsCount,
  description: null,
})

export const deriveLiquidAssets = (
  freeCash: AnnualAggregationMeta,
  assetsBreakdown: readonly FinancialRecordSummary[]
): AnnualAggregationMeta => {
  const liquidItems = assetsBreakdown.filter(a => a.isLiquid)
  return {
    totalValue:
      freeCash.totalValue + liquidItems.reduce((sum, a) => sum + a.value, 0),
    recordsCount: freeCash.recordsCount + liquidItems.length,
    description: null,
  }
}

const aggregateData = (
  timelineRecords: readonly TimelineRecord[]
): AggregatedData => {
  const income = emptyMeta()
  const incomeTaxes = emptyMeta()
  const expenses = emptyMeta()
  const adjustableExpenses = emptyMeta()
  const propertyTaxes = emptyMeta()
  const contributions = emptyMeta()
  const liabilities = emptyMeta()
  const assets = emptyMeta()
  const drawDowns = emptyMeta()
  const freeCash = emptyMeta()
  const assetsBreakdown: FinancialRecordSummary[] = []
  const assetDrawdownMap = new Map<string, AssetDrawdown>()

  const groupMapping = {
    Income: income,
    Expenses: expenses,
    Contributions: contributions,
    Liabilities: liabilities,
    Taxes: incomeTaxes,
    Assets: assets,
  } as const

  timelineRecords.forEach(record => {
    const category = FIN_CATEGORY_BY_KEY.get(record.categoryKey)
    if (!category) return

    const financialRecordId = record.financialRecord?.id
    if (record.intent === 'DrawDown' && financialRecordId) {
      const existingDrawdown = assetDrawdownMap.get(financialRecordId)
      if (existingDrawdown) {
        existingDrawdown.hasDrawdown = true
        existingDrawdown.drawdownAmount += record.value
      } else {
        assetDrawdownMap.set(financialRecordId, {
          hasDrawdown: true,
          drawdownAmount: record.value,
        })
      }
      drawDowns.totalValue += record.value
      drawDowns.recordsCount++
    }

    const group = getCategoryExperience(category).finGroup
    const targetGroup = groupMapping[group as keyof typeof groupMapping]

    if (targetGroup) {
      freeCash.recordsCount++

      if (record.intent !== 'DoubleEntry') {
        targetGroup.totalValue += record.value
        targetGroup.recordsCount++

        /*
         * Two overlapping views of the bucket, neither a partition of it: a
         * loan payment is in neither, being charged by the engine and not a tax.
         *
         * Asked of the record the charge is *against*, not of the row's own
         * category, because the same expense category arrives both ways: a
         * property's top-up is `catExpensesPurchase` charged to the property,
         * and the lever can no more move that than it can a loan's interest.
         */
        if (targetGroup === expenses) {
          const backing = record.financialRecord
          if (backing != null && isAdjustableExpense(backing.finCategory)) {
            adjustableExpenses.totalValue += record.value
            adjustableExpenses.recordsCount++
          }
          if (record.intent === 'Tax') {
            propertyTaxes.totalValue += record.value
            propertyTaxes.recordsCount++
          }
        }
      }

      if (category.key === CategoryKey.catAssetsCash) {
        freeCash.totalValue = record.value
      }
    }

    if (group === 'Assets' && financialRecordId) {
      assetsBreakdown.push({
        id: financialRecordId,
        name: record.financialRecord?.name || financialRecordId,
        value: record.value,
        isLiquid: category.a_isLiquid,
        hasDrawdown:
          assetDrawdownMap.get(financialRecordId)?.hasDrawdown || false,
        drawdownAmount:
          assetDrawdownMap.get(financialRecordId)?.drawdownAmount || 0,
      })
    }
  })

  return {
    income,
    incomeTaxes,
    expenses,
    adjustableExpenses,
    propertyTaxes,
    contributions,
    cashFlow: deriveCashFlow(income, expenses, incomeTaxes, contributions),
    freeCash,
    liabilities,
    assets,
    liquidAssets: deriveLiquidAssets(freeCash, assetsBreakdown),
    drawDowns,
    assetsBreakdown,
  }
}

export default aggregateData
